'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Activity, LayoutDashboard, Mail, Menu, Palette } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { LogoutButton } from '@/components/auth/logout-button';
import { useAuth } from '@/providers/auth-provider';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet';

const navItems = [
  {
    title: 'Sites',
    href: '/sites',
    icon: LayoutDashboard,
  },
  {
    title: 'Emails',
    href: '/emails',
    icon: Mail,
  },
  {
    title: 'UI Demo',
    href: '/ui-demo',
    icon: Palette,
  },
];

export function MainNav() {
  const pathname = usePathname();
  const { isAuthenticated, isLoading } = useAuth();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-14 items-center">
        <div className="mr-4 hidden md:flex">
          <Link href="/" className="mr-6 flex items-center space-x-2">
            <Activity className="h-6 w-6" />
            <span className="hidden font-bold sm:inline-block">ZPM Pulse</span>
          </Link>
          <nav className="flex items-center space-x-6 text-sm font-medium">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  'transition-colors hover:text-foreground',
                  pathname?.startsWith(item.href)
                    ? 'text-foreground'
                    : 'text-muted-foreground'
                )}
              >
                {item.title}
              </Link>
            ))}
          </nav>
        </div>
        <Sheet>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              aria-label="Toggle navigation menu"
              className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 md:hidden"
            >
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle Menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="pr-0">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <MobileNav pathname={pathname} isAuthenticated={isAuthenticated} />
          </SheetContent>
        </Sheet>
        <Link href="/" className="mr-6 flex items-center space-x-2 md:hidden">
          <Activity className="h-6 w-6" />
          <span className="font-bold">ZPM Pulse</span>
        </Link>
        <div className="flex flex-1 items-center justify-end space-x-2">
          <nav className="flex items-center space-x-2">
            <ThemeToggle />
            {isLoading ? (
              <Button disabled variant="outline" size="sm">
                ...
              </Button>
            ) : isAuthenticated ? (
              <LogoutButton variant="outline" />
            ) : (
              <Button asChild>
                <Link href="/login">Login</Link>
              </Button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}

interface MobileNavProps {
  pathname: string | null;
  isAuthenticated: boolean;
}

function MobileNav({ pathname, isAuthenticated }: MobileNavProps) {
  return (
    <div className="flex flex-col space-y-3">
      <Link href="/" className="flex items-center space-x-2">
        <Activity className="h-6 w-6" />
        <span className="font-bold">ZPM Pulse</span>
      </Link>
      <div className="flex flex-col space-y-2">
        {navItems.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            className={cn(
              'flex items-center space-x-2 rounded-md p-2 transition-colors hover:bg-accent',
              pathname?.startsWith(item.href)
                ? 'bg-accent'
                : 'text-muted-foreground'
            )}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.title}</span>
          </Link>
        ))}
      </div>
      <div className="pt-4 border-t">
        {isAuthenticated ? (
          <LogoutButton className="w-full justify-start" />
        ) : (
          <Button asChild className="w-full">
            <Link href="/login">Login</Link>
          </Button>
        )}
      </div>
    </div>
  );
}
